import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Close } from '@mui/icons-material'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, TableContainer } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { MedianDisplay } from '../../../../internal/features/live-events-tracking/components/CurrencyDisplay/MedianDisplay'
import { Currency, CurrencyValueWrapper } from '../../../../internal/types/Currency'

interface CurrencyDialogProps {
  open: boolean
  onClose: () => void
  currencies: Currency[] | undefined
}

type CurrencyRow = {
  id: string
  name: string
  currencyDescription?: string
  purposeDescription?: string
  values: CurrencyValueWrapper[]
  usd?: number
  value?: number
}

const CurrencyDialog: React.FC<CurrencyDialogProps> = ({ open, onClose, currencies }) => {
  const { t } = useTranslation()

  const currencyRows: CurrencyRow[] = useMemo(() => {
    if (!currencies) return []

    return currencies.map((currency, index) => {
      // Handling convert usd/value to values array
      let values: CurrencyValueWrapper[] = []
      if (currency.usd != null && currency.value != null && (!currency.values || currency.values.length === 0)) {
        values = [{ usd: currency.usd, value: currency.value }]
      } else {
        values = currency.values || []
      }

      return {
        id: currency.id || `currency-${index}`,
        name: currency.name,
        currencyDescription: currency.currencyDescription,
        purposeDescription: currency.purposeDescription,
        values,
        usd: currency.usd,
        value: currency.value,
      }
    })
  }, [currencies])

  const currencyColumns: GRTableColumn<CurrencyRow, CurrencyRow>[] = useMemo(
    () => [
      {
        headerCellProps: { sx: { fontWeight: 700, textAlign: 'center' } },
        labelAccessor: () => t('common:name'),
        accessor: ({ row }) => row.name,
        cellProps: { align: 'left', sx: { fontWeight: 700 } },
        sortable: true,
        sortAccessor: ({ row }) => row.name,
        sortOrder: SortOrder.DESC,
      },
      {
        headerCellProps: { sx: { fontWeight: 700, textAlign: 'center', minWidth: '50px' } },
        labelAccessor: () => t('live-events:use'),
        accessor: ({ row }) => row.currencyDescription || '-',
        cellProps: { align: 'left' },
        sortable: false,
      },
      {
        headerCellProps: { sx: { fontWeight: 700, textAlign: 'center' } },
        labelAccessor: () => t('live-events:how_to_get'),
        accessor: ({ row }) => row.purposeDescription || '-',
        cellProps: { align: 'left' },
        sortable: false,
      },
      {
        headerCellProps: { sx: { fontWeight: 700, textAlign: 'center' } },
        labelAccessor: () => t('live-events:conversion_rate'),
        accessor: ({ row }) => {
          if (row.values.length > 0) {
            const currency = { name: row.name, values: row.values }
            return (
              <Box>
                {row.values.map((v, index) => (
                  <Box key={index} sx={{ py: 0.5 }}>
                    ${v.usd} - {v.value}
                  </Box>
                ))}
                <MedianDisplay currency={currency} t={t} fontSize="12px" />
              </Box>
            )
          }
          return '-'
        },
        cellProps: { sx: { textAlign: 'center' } },
        sortable: false,
      },
    ],
    [t]
  )

  const [columns, setColumns] = useState<GRTableColumn<CurrencyRow, CurrencyRow>[]>(currencyColumns)

  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<CurrencyRow, CurrencyRow>[]) => {
    setColumns(updatedColumns)
  }, [])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { minHeight: '60vh', maxHeight: '80vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('live-events:view_all_currencies')}
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: 0, overflowX: 'hidden' }}>
        <TableContainer component={Box} style={{ border: '1px solid #e0e0e0', borderRadius: '20px', marginBottom: '24px', marginTop: '24px' }}>
          <GRTable columns={columns} rows={currencyRows} rowIdKey="id" onColumnsUpdated={handleColumnsUpdate} striped gridlines />
        </TableContainer>
      </DialogContent>
    </Dialog>
  )
}

export default CurrencyDialog
